import { useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
} from '@design-system';

import type { API } from '@/api/types';

type WizardDistributionPartner = API.WizardDistributionPartner;
type WizardDistributionPartnerPatch = API.WizardDistributionPartnerPatch;

import { useWizardDistributionPartners } from './use-wizard-distribution-partners';

function parseDomains(text: string): string[] {
  return text
    .split(',')
    .map(domain => domain.trim().toLowerCase())
    .filter(Boolean);
}

// Logos are stored inline as data URIs, so keep them small. ~700KB raw → ~950KB base64, under the
// server's cap.
const MAX_LOGO_BYTES = 700_000;

function readImageAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

interface LogoFieldProps {
  logoUrl: string | null;
  onChange: (value: string | null) => void;
  idPrefix: string;
}

function LogoField({ logoUrl, onChange, idPrefix }: LogoFieldProps) {
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file?: File) => {
    setError(null);

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }

    if (file.size > MAX_LOGO_BYTES) {
      setError('Image must be under 700 KB.');
      return;
    }

    try {
      onChange(await readImageAsDataUrl(file));
    } catch {
      setError('Failed to read image.');
    }
  };

  return (
    <div className="flex items-center gap-3">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="Logo preview"
          className="h-10 max-w-[6rem] object-contain"
        />
      ) : (
        <span className="text-muted-foreground text-xs">No logo</span>
      )}
      <div className="flex flex-col gap-1">
        <input
          id={`${idPrefix}-logo`}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          onChange={event => handleFile(event.target.files?.[0])}
          className="text-foreground text-xs file:mr-2 file:rounded file:border file:px-2 file:py-1 file:text-xs"
        />
        {logoUrl && (
          <button
            type="button"
            className="text-muted-foreground text-left text-xs underline"
            onClick={() => onChange(null)}
          >
            Remove logo
          </button>
        )}
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  );
}

interface PartnerRowProps {
  partner: WizardDistributionPartner;
  onSave: (id: string, patch: WizardDistributionPartnerPatch) => void;
  isSaving: boolean;
}

function PartnerRow({ partner, onSave, isSaving }: PartnerRowProps) {
  const [displayName, setDisplayName] = useState(partner.displayName);
  const [orgLegalName, setOrgLegalName] = useState(partner.orgLegalName ?? '');
  const [domainsText, setDomainsText] = useState(partner.domains.join(', '));
  const [isActive, setIsActive] = useState(partner.isActive);
  const [isEdjSpecific, setIsEdjSpecific] = useState(partner.isEdjSpecific);
  const [logoUrl, setLogoUrl] = useState<string | null>(partner.logoUrl ?? null);

  const save = () =>
    onSave(partner.id, {
      displayName: displayName.trim(),
      orgLegalName: orgLegalName.trim() || null,
      logoUrl,
      domains: parseDomains(domainsText),
      isActive,
      isEdjSpecific,
    });

  return (
    <div className="flex flex-col gap-3 rounded-md border p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
        <div className="sm:col-span-2">
          <Label className="text-muted-foreground text-xs">Key</Label>
          <p className="text-foreground text-sm font-medium">{partner.partnerKey}</p>
        </div>
        <div className="sm:col-span-3">
          <Label className="text-muted-foreground text-xs">Display name</Label>
          <Input
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
          />
        </div>
        <div className="sm:col-span-4">
          <Label className="text-muted-foreground text-xs">Legal name</Label>
          <Input
            value={orgLegalName}
            onChange={event => setOrgLegalName(event.target.value)}
          />
        </div>
        <div className="sm:col-span-3">
          <Label className="text-muted-foreground text-xs">Domains (comma separated)</Label>
          <Input
            value={domainsText}
            onChange={event => setDomainsText(event.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-col gap-1">
            <Label className="text-muted-foreground text-xs">Logo</Label>
            <LogoField
              logoUrl={logoUrl}
              onChange={setLogoUrl}
              idPrefix={`partner-${partner.id}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`partner-active-${partner.id}`}
              checked={isActive}
              onCheckedChange={value => setIsActive(value === true)}
            />
            <Label
              htmlFor={`partner-active-${partner.id}`}
              className="text-xs"
            >
              Active
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`partner-edj-${partner.id}`}
              checked={isEdjSpecific}
              onCheckedChange={value => setIsEdjSpecific(value === true)}
            />
            <Label
              htmlFor={`partner-edj-${partner.id}`}
              className="text-xs"
            >
              Specific to EDJ
            </Label>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={save}
          disabled={isSaving}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export function PartnerRegistryAdmin() {
  const { partners, isLoading, createPartner, isCreating, updatePartner, isUpdating } =
    useWizardDistributionPartners();

  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newLegal, setNewLegal] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [newLogo, setNewLogo] = useState<string | null>(null);
  const [newEdjSpecific, setNewEdjSpecific] = useState(false);

  const addPartner = () => {
    if (!newKey.trim() || !newName.trim()) return;

    createPartner(
      {
        partnerKey: newKey.trim().toLowerCase(),
        displayName: newName.trim(),
        orgLegalName: newLegal.trim() || null,
        logoUrl: newLogo,
        domains: parseDomains(newDomains),
        isEdjSpecific: newEdjSpecific,
      },
      {
        onSuccess: () => {
          setNewKey('');
          setNewName('');
          setNewLegal('');
          setNewDomains('');
          setNewLogo(null);
          setNewEdjSpecific(false);
        },
      },
    );
  };

  return (
    <Card className="max-w-5xl border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle>Distribution Partners</CardTitle>
        <CardDescription className="text-gray-500">
          Manage the distribution partners advisors can connect with, their email domains, and
          legal names. Domains determine which active accounts appear as connectable contacts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          {isLoading && <p className="text-muted-foreground text-sm">Loading partners…</p>}
          {!isLoading && partners.length === 0 && (
            <p className="text-muted-foreground text-sm">No partners configured yet.</p>
          )}
          {partners.map(partner => (
            <PartnerRow
              key={partner.id}
              partner={partner}
              onSave={(id, patch) => updatePartner({ id, patch })}
              isSaving={isUpdating}
            />
          ))}
        </div>

        <div className="space-y-3 border-t pt-6">
          <p className="text-foreground text-sm font-medium">Add a partner</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-12 sm:items-end">
            <div className="sm:col-span-2">
              <Label className="text-muted-foreground text-xs">Key</Label>
              <Input
                value={newKey}
                placeholder="crump"
                onChange={event => setNewKey(event.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-muted-foreground text-xs">Display name</Label>
              <Input
                value={newName}
                placeholder="Crump"
                onChange={event => setNewName(event.target.value)}
              />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-muted-foreground text-xs">Legal name</Label>
              <Input
                value={newLegal}
                placeholder="Crump Life Insurance Services, LLC"
                onChange={event => setNewLegal(event.target.value)}
              />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-muted-foreground text-xs">Domains</Label>
              <Input
                value={newDomains}
                placeholder="crump.com"
                onChange={event => setNewDomains(event.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="button"
                onClick={addPartner}
                disabled={isCreating || !newKey.trim() || !newName.trim()}
              >
                Add partner
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label className="text-muted-foreground text-xs">Logo (optional)</Label>
              <LogoField
                logoUrl={newLogo}
                onChange={setNewLogo}
                idPrefix="new-partner"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="new-partner-edj"
                checked={newEdjSpecific}
                onCheckedChange={value => setNewEdjSpecific(value === true)}
              />
              <Label
                htmlFor="new-partner-edj"
                className="text-xs"
              >
                Specific to EDJ
              </Label>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
